import React, { useSyncExternalStore } from 'react';
import { Box, Chip, Fade, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MinimizeIcon from '@mui/icons-material/Minimize';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { dismissQaFloatingPanel, getQaProgressSnapshot, subscribeQaProgress } from './qa-progress-store';
import type { QaProgressNode, StepStatus } from './types';

const POS_KEY = 'qa-floating-progress-pos';

function statusIcon(status: StepStatus) {
  const sx = { fontSize: 18 };
  switch (status) {
    case 'pass':
      return <CheckCircleIcon color="success" sx={sx} />;
    case 'fail':
      return <ErrorOutlineIcon color="error" sx={sx} />;
    case 'running':
      return <HourglassEmptyIcon color="info" sx={sx} />;
    case 'skip':
      return <SkipNextIcon color="action" sx={sx} />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" sx={sx} />;
  }
}

function statusLabel(status: StepStatus) {
  switch (status) {
    case 'pass':
      return 'הושלם';
    case 'fail':
      return 'נכשל';
    case 'running':
      return 'רץ…';
    case 'skip':
      return 'דולג';
    default:
      return 'ממתין';
  }
}

function ProgressBranch({ node, depth }: { node: QaProgressNode; depth: number }) {
  const isRunning = node.status === 'running';
  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{
          py: 0.6,
          pr: 1,
          pl: depth * 2 + 1,
          borderRadius: 1,
          bgcolor: isRunning ? 'action.selected' : 'transparent',
          borderInlineStart: depth > 0 ? 2 : 0,
          borderColor: isRunning ? 'primary.main' : 'divider',
          animation: isRunning ? 'qa-breathe 2.2s ease-in-out infinite' : undefined,
          '@keyframes qa-breathe': {
            '0%, 100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.22)' },
            '50%': { boxShadow: '0 0 0 6px rgba(25, 118, 210, 0.06)' },
          },
        }}
      >
        <Box sx={{ pt: 0.15 }}>{statusIcon(node.status)}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            fontWeight={isRunning ? 700 : 500}
            color={isRunning ? 'primary.main' : 'text.primary'}
          >
            {node.title}
          </Typography>
          {node.hint ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {node.hint}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.disabled">
            {statusLabel(node.status)}
          </Typography>
        </Box>
      </Stack>
      {node.children?.length ? (
        <Stack spacing={0}>
          {node.children.map((ch) => (
            <ProgressBranch key={ch.id} node={ch} depth={depth + 1} />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

/** פופאף צף — עץ שלבים חי (מוצג ב-MainLayout) */
const QaFloatingProgress: React.FC = () => {
  const snap = useSyncExternalStore(subscribeQaProgress, getQaProgressSnapshot, getQaProgressSnapshot);
  const [minimized, setMinimized] = React.useState(false);
  const [pos, setPos] = React.useState<{ x: number; y: number }>(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (raw) return JSON.parse(raw) as { x: number; y: number };
    } catch {
      /* ignore */
    }
    return { x: 0, y: 0 };
  });
  const posRef = React.useRef(pos);
  React.useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  const dragRef = React.useRef<{ px: number; py: number; sx: number; sy: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, sx: posRef.current.x, sy: posRef.current.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    setPos({
      x: d.sx + (e.clientX - d.px),
      y: d.sy + (e.clientY - d.py),
    });
  };

  const endDrag = (e: React.PointerEvent) => {
    dragRef.current = null;
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
    } catch {
      /* ignore */
    }
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const hasTree = snap.rootNodes.length > 0;
  const showMini = minimized && (snap.running || hasTree);

  if (!snap.open) {
    return null;
  }

  if (showMini) {
    return (
      <Fade in>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            insetInlineEnd: 16,
            bottom: 16,
            zIndex: 1400,
            px: 1.5,
            py: 0.75,
            cursor: 'pointer',
            borderRadius: 2,
          }}
          onClick={() => setMinimized(false)}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <HourglassEmptyIcon color={snap.running ? 'info' : 'disabled'} fontSize="small" />
            <Typography variant="caption" fontWeight={600}>
              {snap.running ? 'QA רץ…' : 'QA — לחץ להרחבה'}
            </Typography>
          </Stack>
        </Paper>
      </Fade>
    );
  }

  if (!hasTree && !snap.running) {
    return null;
  }

  return (
    <Fade in>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          insetInlineEnd: 24,
          bottom: 24,
          width: 360,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'min(72vh, 520px)',
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: `translate(${pos.x}px, ${pos.y}px)`,
          border: 1,
          borderColor: 'divider',
        }}
        data-qa-floating-progress="1"
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            px: 1,
            py: 0.75,
            bgcolor: 'primary.dark',
            color: 'primary.contrastText',
            cursor: 'grab',
            userSelect: 'none',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
        >
          <DragIndicatorIcon sx={{ opacity: 0.85 }} />
          <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {snap.scenarioTitle ?? 'בדיקת QA'}
          </Typography>
          <Chip
            size="small"
            label={snap.pace === 'human' ? 'קצב אנושי' : 'רגיל'}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', height: 22 }}
          />
          <Tooltip title="מזעור">
            <IconButton size="small" onClick={() => setMinimized(true)} sx={{ color: 'inherit' }}>
              <MinimizeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="סגירת החלון (הריצה ממשיכה)">
            <IconButton size="small" onClick={() => dismissQaFloatingPanel()} sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box sx={{ px: 1.5, py: 1, overflow: 'auto', flex: 1 }}>
          {!hasTree && snap.running && (
            <Typography variant="body2" color="text.secondary">
              טוען שלבים…
            </Typography>
          )}
          <Stack spacing={0.5}>
            {snap.rootNodes.map((n) => (
              <ProgressBranch key={n.id} node={n} depth={0} />
            ))}
          </Stack>
          {snap.running ? (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
              הריצה פעילה — גרירה מהכותרת
            </Typography>
          ) : null}
        </Box>
      </Paper>
    </Fade>
  );
};

export default QaFloatingProgress;
